import React, { useEffect } from 'react';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Card, CardContent } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import FormField from '../../components/FormField';
import { filterPincodeInput } from '../../commonUtils/string.helper';
import { useSelector, useDispatch } from 'react-redux';
import constants from '../../commonUtils/constants';
import { setExtractionData, getExtMetaData, getExtProjectsForMaster } from '../../store/extractions/action';
import LabelTooltip from '../../components/LabelTooltip';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import OtherDetailsSection from './OtherDetailsSection';
import { getExtPincodeFromMaster } from '../../store/extractions/action';

const Step1 = () => {
    const dispatch = useDispatch();
    const { pageLoadData, extractionData, extErrors: errors, projectsLoading, metaDataLoading, pageLoadDataLoading } = useSelector((state) => state.Extractions);
    
    useEffect(() => {
        if (extractionData?.pincode) {
            dispatch(getExtPincodeFromMaster(extractionData.pincode));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleInputChange = (field, value) => {
        // Special handling for pincode: filter input to digits and max 6 chars
        if (field === 'pincode') {
            value = filterPincodeInput(value);
        }
        // Start with current extraction data
        let updatedData = {
            ...extractionData,
            [field]: value
        };
        
        // Handle specific field logic
        if (field === 'addTemporaryValues' && value === true) {
            updatedData = {
                ...updatedData,
                masterProject: '',
                project: ''
            };
        } else if (field === 'addTemporaryValues' && value === false) {
            updatedData = {
                ...updatedData,
                tempMasterProject: '',
                tempProject: ''
            };
        }
        
        // Dispatch the updated data
        dispatch(setExtractionData(updatedData));
        
        // Call projects API only after state update for masterProject
        if (field === "masterProject" && value && value !== "") {
            updatedData.project = '';
            dispatch(getExtProjectsForMaster(value));
        }
        
        // Trigger metadata API call when both masterProject and project are selected
        // masterProjects and projects are arrays of strings, so the value IS the name
        if (field === "project" && value && value !== "") {
            const currentMasterProject = extractionData.masterProject;
            if (currentMasterProject && currentMasterProject !== "") {
                dispatch(getExtMetaData({
                    masterProject: currentMasterProject,
                    projectName: value
                }));
            }
        } else if (field === "masterProject" && value && value !== "") {
            // If masterProject changes and project is already selected, retrigger metadata
            if (extractionData.project && extractionData.project !== "") {
                dispatch(getExtMetaData({
                    masterProject: value,
                    projectName: extractionData.project
                }));
            }
        }
        // else if (field === "pincode" && value && value.length >= 2) {
        //     dispatch(getExtPincodeFromMaster(value));
        // }
    };
    
    return (
        <Card>
            <CardContent className="p-6 space-y-6">
                {pageLoadDataLoading && (
                    <div className="flex items-center justify-center py-4">
                        <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        <span className="ml-2 text-sm text-gray-400">Loading...</span>
                    </div>
                )}
                
                {/* <Label className="font-bold text-gray-500 text-lg">Project Details</Label> */}

                {/* Dropdown Selection Section */}
                <div 
                    className={`space-y-6 transition-all duration-300 ease-in-out overflow-hidden ${
                        extractionData.addTemporaryValues 
                            ? 'max-h-0 opacity-0 pointer-events-none' 
                            : 'max-h-[40rem] opacity-100'
                    }`}
                >
                    {/* Master Project Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                        <Label htmlFor="masterProjectId" className="font-bold text-gray-500">
                            Master Project *
                            <LabelTooltip
                                Icon={InformationCircleIcon}
                                content={
                                    <span className="ml-auto max-w-[400px]">
                                        {constants.extraction.tooltipTexts?.masterProject}
                                    </span>
                                }
                            />
                        </Label>
                        <div className="md:col-span-2">
                            <FormField
                                fieldKey="masterProject"
                                label="Master Project"
                                hideLabel={true}
                                type="list"
                                required={true}
                                data={pageLoadData?.masterProjects || []}
                                value={extractionData.masterProject}
                                error={errors?.masterProject}
                                handleChange={handleInputChange}
                                disabled={extractionData.addTemporaryValues}
                            />
                        </div>
                    </div>
                    
                    {/* Project Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                        <Label htmlFor="projectId" className="font-bold text-gray-500">
                            Project *
                            <LabelTooltip
                                Icon={InformationCircleIcon}
                                content={
                                    <span className="ml-auto max-w-[400px]">
                                        {constants.extraction.tooltipTexts?.project}
                                    </span>
                                }
                            />
                        </Label>
                        <div className="md:col-span-2">
                            <FormField
                                fieldKey="project"
                                label="Project"
                                hideLabel={true}
                                type="list"
                                required={true}
                                data={pageLoadData?.projects || []}
                                value={extractionData.project}
                                error={errors?.project}
                                handleChange={handleInputChange}
                                tooltipText={constants.extraction.tooltipTexts?.project}
                                loading={projectsLoading && !!extractionData.masterProject}
                                disabled={extractionData.addTemporaryValues || !extractionData.masterProject}
                            />
                        </div>
                    </div>

                    {/* Other Details Section */}
                    <OtherDetailsSection
                        extractionData={extractionData}
                        errors={errors}
                        handleInputChange={handleInputChange}
                        metaDataLoading={metaDataLoading}
                    />
                    
                    {/* OR Separator */}
                    <div className="flex items-center gap-4 py-4">
                        <Separator className="flex-1" />
                        <span className="text-slate-500 font-medium bg-white px-2">OR</span>
                        <Separator className="flex-1" />
                    </div>
                </div>
                
                {/* Temporary Values Checkbox */}
                <div className="flex items-center space-x-3">
                    <Checkbox
                        id="tempValues"
                        checked={extractionData.addTemporaryValues}
                        onCheckedChange={(checked) => handleInputChange('addTemporaryValues', checked)}
                    />
                    <Label htmlFor="tempValues" className="text-slate-700 font-medium cursor-pointer">
                        Add your own Temporary Values (you can update them later)
                    </Label>
                </div>
                
                {/* Temporary Input Fields */}
                <div 
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                        extractionData.addTemporaryValues 
                            ? 'max-h-96 opacity-100' 
                            : 'max-h-0 opacity-0'
                    }`}
                >
                    <Card className="bg-slate-50 border-slate-200">
                        <CardContent className="p-6 space-y-6">
                            {/* Temp Master Project */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <Label htmlFor="tempMasterProject" className="text-slate-700 font-medium">
                                    Master Project *
                                    <LabelTooltip
                                        Icon={InformationCircleIcon}
                                        content={
                                            <span className="ml-auto max-w-[400px]">
                                                {constants.extraction.tooltipTexts?.masterProject}
                                            </span>
                                        }
                                    />
                                </Label>
                                <div className="md:col-span-2">
                                    <FormField
                                        fieldKey="tempMasterProject"
                                        label="Master Project"
                                        type="text"
                                        value={extractionData.tempMasterProject}
                                        error={errors?.tempMasterProject}
                                        handleChange={handleInputChange}
                                        disabled={false}
                                    />
                                </div>
                            </div>
                            
                            {/* Temp Project */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <Label htmlFor="tempProject" className="text-slate-700 font-medium">
                                    Project *
                                    <LabelTooltip
                                        Icon={InformationCircleIcon}
                                        content={
                                            <span className="ml-auto max-w-[400px]">
                                                {constants.extraction.tooltipTexts?.project}
                                            </span>
                                        }
                                    />
                                </Label>
                                <div className="md:col-span-2">
                                    <FormField
                                        fieldKey="tempProject"
                                        label="Project"
                                        type="text"
                                        value={extractionData.tempProject}
                                        error={errors?.tempProject}
                                        handleChange={handleInputChange}
                                        disabled={false}
                                    />
                                </div>
                            </div>

                            {/* Other Details Section */}
                            <OtherDetailsSection
                                extractionData={extractionData}
                                errors={errors}
                                handleInputChange={handleInputChange}
                                metaDataLoading={metaDataLoading}
                            />
                        </CardContent>
                    </Card>
                </div>
            </CardContent>
        </Card>
    );
};

export default Step1;